import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiAddLine, RiFileTextLine } from '@remixicon/react'
import type { ArtifactListItem } from 'shared'
import { Alert, Badge, Button, Card, EmptyState, Input, SectionSpinner } from '../ui'
import PanelRow from './PanelRow'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'
import { ARTIFACT_STATUS_TONE } from '../../lib/status'
import { formatDate } from '../../lib/dates'

export default function WorkspaceReportsPanel({ workspaceId }: { workspaceId: string }) {
  const { t, i18n } = useTranslation()

  const [artifacts, setArtifacts] = useState<ArtifactListItem[]>([])
  const [title, setTitle] = useState('')
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const response = await api.get<{ data: ArtifactListItem[] }>(
      `/api/workspaces/${workspaceId}/artifacts`,
    )
    setArtifacts(response.data)
  }, [workspaceId])

  useEffect(() => {
    load()
      .catch((err) => setError(toErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [load])

  const create = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!title.trim()) return

    setCreating(true)
    setError(null)

    try {
      await api.post(`/api/workspaces/${workspaceId}/artifacts`, { title: title.trim() })
      setTitle('')
      await load()
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    } finally {
      setCreating(false)
    }
  }

  if (loading) return <SectionSpinner />

  return (
    <div className="space-y-6">
      {error && <Alert>{error}</Alert>}

      <Card className="p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
          {t('reports.newTitle')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('reports.newDescription')}
        </p>

        <form onSubmit={create} className="mt-4 flex flex-col gap-3 sm:flex-row">
          <div className="sm:flex-1">
            <Input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={t('reports.titlePlaceholder')}
              aria-label={t('reports.titlePlaceholder')}
            />
          </div>
          <Button type="submit" loading={creating} disabled={!title.trim()}>
            <RiAddLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
            {t('reports.create')}
          </Button>
        </form>
      </Card>

      {artifacts.length === 0 ? (
        <Card>
          <EmptyState
            icon={<RiFileTextLine className="size-6" aria-hidden="true" />}
            title={t('reports.emptyTitle')}
            description={t('reports.emptyDescription')}
          />
        </Card>
      ) : (
        <Card className="divide-y divide-gray-100 dark:divide-gray-800">
          {artifacts.map((artifact) => (
            <PanelRow
              key={artifact.id}
              to={`/app/workspaces/${workspaceId}/reports/${artifact.id}`}
              kind="report"
              icon={<RiFileTextLine className="size-5" aria-hidden="true" />}
              title={artifact.title}
              meta={
                <>
                  {t('reports.versionCount', { count: artifact.version_count })}
                  {artifact.published_at &&
                    ` \u00b7 ${formatDate(artifact.published_at, i18n.language)}`}
                </>
              }
              badges={
                <>
                  {artifact.created_by_agent && (
                    <Badge variant="default">{t('timeline.byAgent')}</Badge>
                  )}
                  <Badge variant={ARTIFACT_STATUS_TONE[artifact.status]}>
                    {t(`reports.status_${artifact.status}`)}
                  </Badge>
                </>
              }
            />
          ))}
        </Card>
      )}
    </div>
  )
}
